import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut as fbSignOut, type User } from "firebase/auth";
import { auth, firebaseAuthError, isStubAuthEnabled } from "@/lib/firebase";

interface AuthState {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isStub: boolean;
  authError: string | null;
  /** Stub-mode login: sets the user from the login form. */
  stubLogin: (email: string) => void;
}

export const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  signOut: async () => {},
  isStub: false,
  authError: null,
  stubLogin: () => {},
});

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

function makeStubUser(email: string): User {
  const name = email.split("@")[0] || "Admin";
  return { uid: "stub-user", email, displayName: name } as User;
}

export function useAuthState(): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isStub = isStubAuthEnabled;

  useEffect(() => {
    if (isStub) {
      // Stub mode: user starts as null (login screen will show).
      // Check sessionStorage for a previously logged-in stub session.
      const saved = sessionStorage.getItem("stub_user_email");
      if (saved) {
        setUser(makeStubUser(saved));
      }
      setLoading(false);
      return;
    }
    if (!auth) {
      setLoading(false);
      return;
    }
    const firebaseAuth = auth;
    let resolved = false;
    const fallbackTimer = window.setTimeout(() => {
      if (resolved) return;
      resolved = true;
      setUser(firebaseAuth.currentUser);
      setLoading(false);
    }, 4000);
    const unsubscribe = onAuthStateChanged(firebaseAuth, (firebaseUser) => {
      resolved = true;
      window.clearTimeout(fallbackTimer);
      setUser(firebaseUser);
      setLoading(false);
    });
    return () => {
      window.clearTimeout(fallbackTimer);
      unsubscribe();
    };
  }, [isStub]);

  const stubLogin = useCallback((email: string) => {
    if (!isStub) return;
    const u = makeStubUser(email);
    sessionStorage.setItem("stub_user_email", email);
    setUser(u);
  }, [isStub]);

  const signOut = useCallback(async () => {
    if (auth) {
      await fbSignOut(auth);
    } else if (isStub) {
      // Stub mode: clear session
      sessionStorage.removeItem("stub_user_email");
      setUser(null);
    }
  }, [isStub]);

  return { user, loading, signOut, isStub, authError: firebaseAuthError, stubLogin };
}
